// src/pages/Chat/UserProfileModal.jsx
import React, { useState, useEffect } from "react";
import { FiX, FiMail, FiMapPin, FiBriefcase, FiUser, FiCalendar, FiLoader } from "react-icons/fi";
import { resolvePhotoUrl } from "../../utils/photoHelper";
import { getUserById } from "../../api/accountsService";
import { getPresence } from "../../api/chatService";
import useChatStore from "../../store/chatStore";

function UserAvatar({ photo, name }) {
  const [imgErr, setImgErr] = useState(false);
  const resolvedPhoto = resolvePhotoUrl(photo);

  if (resolvedPhoto && !imgErr) {
    return (
      <img
        src={resolvedPhoto}
        alt={name}
        className="w-full h-full object-cover"
        onError={() => setImgErr(true)}
      />
    );
  }
  const initials = name
    ? name.split(" ").map(n => n[0]).join("").toUpperCase()
    : "?";
  return <>{initials}</>;
}

export default function UserProfileModal({ user: initialUser, onClose }) {
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(false);
  const [presence, setPresence] = useState({ status: "OFFLINE", last_seen: null });

  const liveStatus = useChatStore((s) => initialUser?.employee_id ? s.presence[initialUser.employee_id] : undefined);

  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!initialUser?.employee_id) return;
      setLoading(true);
      try {
        const { data } = await getUserById(initialUser.employee_id);
        const u = data.user || data;
        const resolvedPhoto = u.photo || u.profile_photo || data.user?.photo || data.user?.profile_photo || data.photo || data.profile_photo || null;

        setUser({
          ...initialUser,
          ...u,
          full_name: u.full_name || initialUser.full_name,
          email: u.email || initialUser.email,
          role: u.role || u.designation || initialUser.role,
          department: u.department || u.dept || initialUser.department,
          phone: u.mobile_number || u.phone_number || u.phone || initialUser.phone,
          photo: resolvedPhoto || initialUser.photo,
          location: u.location || initialUser.location
        });

        // Also fetch presence details
        const presRes = await getPresence([initialUser.employee_id]);
        const presData = presRes.data?.presence?.[initialUser.employee_id];
        if (presData) {
          setPresence({
            status: presData.status || "OFFLINE",
            last_seen: presData.last_seen || null
          });
        }
      } catch (err) {
        console.error("Failed to fetch full profile:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFullProfile();
  }, [initialUser]);

  useEffect(() => {
    if (liveStatus) {
      setPresence(prev => ({
        ...prev,
        status: liveStatus
      }));
    }
  }, [liveStatus]);

  if (!user) return null;

  const isOnline = presence.status === "ONLINE" || presence.status === "AWAY";

  const formatLastActive = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const dateObj = new Date(isoString);
      if (isNaN(dateObj.getTime())) return "N/A";

      const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const dateStr = dateObj.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

      return `${timeStr} on ${dateStr}`;
    } catch {
      return "N/A";
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Container */}
      <div className="fixed z-[101] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[500px] px-4 animate-in fade-in zoom-in duration-200">
        <div className="bg-[#1e1e1e] border border-white/10 rounded-2xl shadow-2xl overflow-hidden text-slate-100">

          {/* Header Section */}
          <div className="relative p-6 pb-4 border-b border-white/5">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors"
            >
              <FiX size={20} />
            </button>

            <div className="flex items-center gap-5">
              {/* Large Avatar */}
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-300 to-pink-450 flex items-center justify-center text-2xl font-bold text-slate-800 shadow-lg overflow-hidden border-2 border-white/10">
                  <UserAvatar key={user.photo || "no-photo"} photo={user.photo} name={user.full_name} />
                </div>
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
                    <FiLoader className="animate-spin text-white" />
                  </div>
                )}
                <div className={`absolute bottom-0 right-0 w-5.5 h-5.5 rounded-full ${isOnline ? "bg-green-500" : "bg-slate-500"} border-4 border-[#1e1e1e] flex items-center justify-center`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                </div>
              </div>

              {/* Basic Info (Removed the Action Icons below this section per user request) */}
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-white mb-1 truncate">{user.full_name}</h2>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">{user.role || "Employee"}</p>
              </div>
            </div>
          </div>

          {/* Details Overview Area (Removed Contact & Organization sections, placed all in Overview) */}
          <div className="p-6 max-h-[380px] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
            <div className="space-y-5">

              {/* Status Bar */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                {isOnline ? (
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-300">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="font-semibold">Available • Active Now</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-300">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-500" />
                    <span className="font-semibold text-slate-400">
                      Not available - last active at {formatLastActive(presence.last_seen)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-2 ml-5.5">
                  <FiCalendar size={12} className="text-blue-400" />
                  <span>Work hours: 10:00 AM - 07:00 PM</span>
                </div>
              </div>

              {/* Employee Details Grid */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Location */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 shrink-0">
                      <FiMapPin size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Location</p>
                      <p className="text-sm font-medium text-slate-200 truncate">{user.location || "Main Office"}</p>
                    </div>
                  </div>

                  {/* Department */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400 shrink-0">
                      <FiBriefcase size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Department</p>
                      <p className="text-sm font-medium text-slate-200 truncate">{user.department || "N/A"}</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 shrink-0">
                      <FiMail size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Email</p>
                      <a href={`mailto:${user.email}`} className="text-sm font-medium text-blue-400 hover:underline block truncate" title={user.email}>
                        {user.email || "N/A"}
                      </a>
                    </div>
                  </div>

                  {/* Employee ID */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center text-pink-400 shrink-0">
                      <FiUser size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Employee ID</p>
                      <p className="text-sm font-mono text-slate-200 truncate">{user.employee_id || "N/A"}</p>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
